import React, { useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Copy, Check, ExternalLink } from 'lucide-react'
import { EXTERNAL_LINKS } from '../utils/constants'

const configs = {
  claude: `{
  "mcpServers": {
    "congressmcp": {
      "command": "uvx",
      "args": ["congressmcp"],
      "env": {
        "CONGRESS_API_KEY": "your-api-key-here"
      }
    }
  }
}`,
  vscode: `{
  "mcp": {
    "servers": {
      "congressmcp": {
        "command": "uvx",
        "args": ["congressmcp"],
        "env": {
          "CONGRESS_API_KEY": "your-api-key-here"
        }
      }
    }
  }
}`,
  cursor: `{
  "mcpServers": {
    "congressmcp": {
      "command": "uvx",
      "args": ["congressmcp"],
      "env": {
        "CONGRESS_API_KEY": "your-api-key-here"
      }
    }
  }
}`
}

const SetupGuide = () => {
  const [copiedId, setCopiedId] = useState(null)
  const [activeTab, setActiveTab] = useState('claude')

  const handleCopy = useCallback(async (text, id) => {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      // silent fail
    }
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }, [])

  const CopyButton = ({ text, id }) => (
    <button
      onClick={() => handleCopy(text, id)}
      className="absolute top-2 right-2 p-2 bg-primary-700/50 hover:bg-primary-600/50 rounded transition-colors"
      aria-label="Copy"
    >
      {copiedId === id ? <Check size={16} className="text-green-400" /> : <Copy size={16} className="text-primary-300" />}
    </button>
  )

  return (
    <div className="min-h-screen bg-primary-900 noise-bg">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="glass-button">
              &larr; Back to Home
            </Link>
            <h1 className="heading-xl text-center flex-1">Setup Guide</h1>
            <div className="w-32"></div>
          </div>

          <div className="space-y-8">
            {/* Step 1 */}
            <div className="glass-card overflow-visible">
              <h2 className="heading-md mb-4">1. Get a Congress.gov API Key</h2>
              <p className="body-sm text-text-secondary mb-4">
                Congress.gov provides free API keys. It takes about 30 seconds to sign up.
              </p>
              <a
                href={EXTERNAL_LINKS.CONGRESS_API_SIGNUP}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 glass-button-primary px-4 py-2 rounded-lg text-sm font-medium"
              >
                Get Free API Key <ExternalLink size={14} />
              </a>
            </div>

            {/* Step 2 */}
            <div className="glass-card overflow-visible">
              <h2 className="heading-md mb-4">2. Install CongressMCP</h2>
              <p className="body-sm text-text-secondary mb-4">
                Recommended: use{' '}
                <a href={EXTERNAL_LINKS.UV_DOCS} target="_blank" rel="noopener noreferrer" className="text-accent-blue hover:underline">uv</a>{' '}
                (Python 3.10+ required)
              </p>
              <div className="relative mb-4">
                <pre className="bg-primary-800/50 p-4 rounded-lg text-sm text-text-primary overflow-x-auto">
                  uvx congressmcp
                </pre>
                <CopyButton text="uvx congressmcp" id="install-uvx" />
              </div>
              <p className="body-sm text-text-muted mb-2">Or with pip:</p>
              <div className="relative">
                <pre className="bg-primary-800/50 p-4 rounded-lg text-sm text-text-primary overflow-x-auto">
                  pip install congressmcp
                </pre>
                <CopyButton text="pip install congressmcp" id="install-pip" />
              </div>
            </div>

            {/* Step 3 */}
            <div className="glass-card overflow-visible">
              <h2 className="heading-md mb-4">3. Configure Your Client</h2>
              <p className="body-sm text-text-secondary mb-4">
                Add CongressMCP to your MCP client config. Replace{' '}
                <code className="text-text-primary bg-primary-800/30 px-1 rounded">your-api-key-here</code>{' '}
                with your Congress.gov API key.
              </p>

              <div className="flex gap-2 mb-4">
                {[
                  { id: 'claude', label: 'Claude Desktop' },
                  { id: 'vscode', label: 'VS Code' },
                  { id: 'cursor', label: 'Cursor' }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'bg-accent-blue/20 text-accent-blue border border-accent-blue/30'
                        : 'bg-primary-800/30 text-text-muted hover:text-text-secondary'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {activeTab === 'claude' && (
                <p className="body-sm text-text-muted mb-3">
                  <span className="text-primary-400 font-medium">Mac:</span>{' '}
                  <code className="text-text-secondary bg-primary-800/30 px-2 py-1 rounded">~/Library/Application Support/Claude/claude_desktop_config.json</code>
                  <br />
                  <span className="text-primary-400 font-medium">Windows:</span>{' '}
                  <code className="text-text-secondary bg-primary-800/30 px-2 py-1 rounded">%APPDATA%\Claude\claude_desktop_config.json</code>
                </p>
              )}
              {activeTab === 'vscode' && (
                <p className="body-sm text-text-muted mb-3">
                  Add to your VS Code <code className="text-text-secondary bg-primary-800/30 px-2 py-1 rounded">settings.json</code>
                </p>
              )}
              {activeTab === 'cursor' && (
                <p className="body-sm text-text-muted mb-3">
                  Add to your Cursor MCP configuration file.
                </p>
              )}

              <div className="relative">
                <pre className="bg-primary-800/50 p-4 rounded-lg text-sm text-text-primary overflow-x-auto">
{configs[activeTab]}
                </pre>
                <CopyButton text={configs[activeTab]} id={`config-${activeTab}`} />
              </div>

              <p className="body-sm text-text-muted mt-4">
                Save the file and restart your client.
              </p>
            </div>

            {/* Step 4 */}
            <div className="glass-card overflow-visible">
              <h2 className="heading-md mb-4">4. Self-Hosting (Optional)</h2>
              <p className="body-sm text-text-secondary mb-4">
                To run CongressMCP as a remote server instead of locally:
              </p>
              <div className="relative">
                <pre className="bg-primary-800/50 p-4 rounded-lg text-sm text-text-primary overflow-x-auto">
                  uvx congressmcp --transport streamable-http --port 8000
                </pre>
                <CopyButton text="uvx congressmcp --transport streamable-http --port 8000" id="self-host" />
              </div>
              <p className="body-sm text-text-muted mt-3">
                This starts a stateless HTTP server that any MCP client can connect to remotely.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SetupGuide
